import { useContext, useEffect, useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useNavigate } from "react-router-dom";
import { getEventById, getEventsByFilter } from "../../../Services/Event";
import { getFirmById } from "../../../Services/Firm";
import { getGroupMembershipsByPersonId } from "../../../Services/GroupMembership";
import {
  getInvoiceHistory,
  IInvoiceHistory,
  listInvoiceFiles,
} from "../../../Services/Invoice";
import { getRegistrationsByPerson } from "../../../Services/Registration";
import inPersonImage from "../../../assets/images&icons/in-person.jpg";
import hybridImage from "../../../assets/images&icons/hibrid.jpg";
import virtualImage from "../../../assets/images&icons/virtual.jpg";
import { UseGroupList } from "../../../hooks/Catalogs/useGroupList";
import { IEvent } from "../../../interfaces/models/IEvent";
import { IRegistration } from "../../../interfaces/models/IRegistration";
import AuthContext from "../../../store/AuthContext";
import {
  getEventCountdownBadge,
  isCurrentEvent,
} from "../../../utils/eventTiming";
import { downloadInvoicePDF } from "../../../utils/generateInvoicePDF";
import { formatEventDate as formatEventDateDisplay } from "../../../utils/eventDateFormatter";
import { formatEventLocation } from "../../../utils/eventLocationFormatter";
import { toastError, toastWarn } from "../../../utils/toast";
import { getAllGroups } from "../../../Services/Group";
import { getPreferenceEvents } from "./myEventsPreferenceUtils";

interface EventCardData {
  event: IEvent;
  registration?: IRegistration;
}

interface BlobInvoiceFile {
  name: string;
  url: string;
  dateCreated?: string;
}

type InvoiceCandidate = Omit<IInvoiceHistory, "invoiceUrl"> & {
  invoice?: string;
  invoiceUrl?: string;
};

interface EventCardProps {
  data: EventCardData;
  groupData: any[];
  isSingleLayout: boolean;
  isDownloading: boolean;
  onDownloadInvoice: (data: EventCardData) => Promise<void>;
  onRegister: (eventId: string) => void;
  onModifyRegistration: (eventId: string) => void;
  onCompletePayment: (eventId: string) => void;
}

interface EventListSectionProps {
  title: string;
  events: IEvent[];
  onRegister: (eventId: string) => void;
  loading: boolean;
  emptyMessage: string;
}

const TEN_YEARS_IN_SECONDS = Math.floor(
  new Date(new Date().getFullYear() + 10, 0, 1).getTime() / 1000,
);
const ONE_DAY_IN_SECONDS = 24 * 60 * 60;
const WSG_UPCOMING_VISIBILITY_FILTER = {
  status: {
    field: "status",
    value: "online",
    comparisonType: "EQUAL",
  },
  memberEventsTab: {
    field: "memberEventsTab",
    value: true,
    comparisonType: "NOT_EQUAL_OR_NULL",
  },
  upcomingEventPage: {
    field: "upcomingEventPage",
    value: true,
    comparisonType: "EQUAL",
  },
};
const REGISTERED_EVENTS_CAROUSEL_RESPONSIVE = {
  desktop: {
    breakpoint: { max: 3000, min: 1200 },
    items: 2,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1200, min: 1024 },
    items: 1,
    slidesToSlide: 1,
  },
  stacked: {
    breakpoint: { max: 1023, min: 650 },
    items: 2,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 649, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};
const REGISTERED_EVENTS_AUTOPLAY_SPEED = 5000;
const MAX_UPCOMING_EVENTS = 2;
const MAX_PREFERENCE_EVENTS = 2;

const PAID_REGISTRATION_STATUSES = new Set([
  "completed",
  "paid",
  "paidspk",
  "paidspr",
]);

const IconCalendar = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconDownload = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const IconLocation = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 21s-6-4.35-6-10a6 6 0 1 1 12 0c0 5.65-6 10-6 10Z" />
    <circle cx="12" cy="11" r="2.5" />
  </svg>
);

const IconChevronRight = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CarouselNavButton = ({
  direction,
  onClick,
}: {
  direction: "left" | "right";
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === "left" ? "Previous event" : "Next event"}
    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d6dde5] bg-white text-[#0d67c4] shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors hover:border-[#0d67c4] hover:bg-[#f3f8ff]"
  >
    <IconChevronRight
      className={`h-4 w-4 ${direction === "left" ? "rotate-180" : ""}`}
    />
  </button>
);

const normalizeStatus = (status?: string): string =>
  (status || "").trim().toLowerCase();

const isActiveRegistration = (registration: IRegistration): boolean =>
  registration.isDeleted !== true &&
  normalizeStatus(registration.status) !== "cancelled";

const isPaidRegistration = (paymentStatus?: string): boolean =>
  PAID_REGISTRATION_STATUSES.has(normalizeStatus(paymentStatus));

const getInvoiceLabel = (paymentStatus?: string): string =>
  isPaidRegistration(paymentStatus)
    ? "Download Paid Invoice"
    : "Download Invoice";

const getInvoiceTypeForStatus = (paymentStatus?: string): "Paid" | "Due" =>
  isPaidRegistration(paymentStatus) ? "Paid" : "Due";

const shouldShowPaymentPending = (paymentStatus?: string): boolean =>
  !isPaidRegistration(paymentStatus);

const isEnabledEventSetting = (value: unknown): boolean =>
  value === true || value === 1;

const shouldShowMyWsgEvent = (event: IEvent): boolean =>
  event.status === "online" &&
  !isEnabledEventSetting(event.memberEventsTab) &&
  isEnabledEventSetting(event.upcomingEventPage);

const resolveDisplayTimeZone = (
  timeZone?: string | null,
): string | undefined => {
  if (!timeZone || timeZone === "UTC") {
    return "UTC";
  }

  if (timeZone.includes("_") || /^UTC[+-]\d{1,2}:\d{2}$/.test(timeZone)) {
    return undefined;
  }

  return timeZone;
};

const formatMyWsgEventDate = (
  event: IEvent,
  formatter: (timestamp: number, timeZone?: string | null) => string,
): string => {
  if (event.comingSoonDate || event.startDate == null) {
    return formatEventDateDisplay(event);
  }

  return formatter(event.startDate, event.timeZone);
};

const formatEventDateInTimeZone = (
  timestamp?: number,
  timeZone?: string | null,
): string => {
  if (!timestamp) {
    return "Date TBD";
  }

  const date = new Date(timestamp * 1000);
  const displayTimeZone = resolveDisplayTimeZone(timeZone);

  try {
    return new Intl.DateTimeFormat("en-US", {
      // weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(displayTimeZone ? { timeZone: displayTimeZone } : {}),
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      // weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }
};

const formatSectionDate = (
  timestamp?: number,
  timeZone?: string | null,
): string => {
  if (!timestamp) {
    return "Date TBD";
  }

  const date = new Date(timestamp * 1000);
  const displayTimeZone = resolveDisplayTimeZone(timeZone);

  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...(displayTimeZone ? { timeZone: displayTimeZone } : {}),
    })
      .format(date)
      .replace(/ (\d{4})$/, ", $1");
  } catch {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
      .format(date)
      .replace(/ (\d{4})$/, ", $1");
  }
};

const getDefaultEventImage = (event: IEvent): string => {
  if (event.eventType === "virtual") {
    return virtualImage;
  }

  if (event.eventType === "hybrid") {
    return hybridImage;
  }

  return inPersonImage;
};

const getRegisteredEventImage = (
  event: IEvent,
  groupPhotoFallback: string | null,
  groupFallback: string | null,
): string => {
  return (
    event.bannerLists ||
    event.bannerDetails ||
    event.bannerHomePage ||
    groupPhotoFallback ||
    groupFallback ||
    event.image ||
    event.coverImage ||
    event.photoGallery?.[0] ||
    event.accomodation?.[0]?.image ||
    getDefaultEventImage(event)
  );
};

const getGroupFallbackImage = (
  event: IEvent,
  groupData: any[],
): string | null => {
  if (!groupData.length) {
    return null;
  }
  const groupedSponsor = event.sponsorHost?.find(
    (sponsor) => sponsor.type === "wsg_group",
  );

  if (groupedSponsor) {
    const groupById = groupData.find(
      (group: any) => group.id === groupedSponsor.id,
    );
    if (groupById?.photo) {
      return groupById.photo;
    }
  }

  if (event.groupIds?.length) {
    const groupByGroupId = groupData.find(
      (group: any) =>
        event.groupIds?.includes(group.id) ||
        event.groupIds?.includes(group.title),
    );
    if (groupByGroupId?.photo) {
      return groupByGroupId.photo;
    }
  }

  if (event.sponsorHost?.length) {
    const firstSponsor = event.sponsorHost[0];
    const groupByTitle = groupData.find(
      (group: any) =>
        group.id === firstSponsor.id || group.title === firstSponsor.id,
    );
    if (groupByTitle?.photo) {
      return groupByTitle.photo;
    }
  }

  return null;
};

const mergeRegistrationsByEvent = (
  registrations: IRegistration[],
): Map<string, IRegistration> => {
  const registrationsByEvent = new Map<string, IRegistration>();

  registrations.filter(isActiveRegistration).forEach((registration) => {
    if (!registration.eventId) {
      return;
    }

    const current = registrationsByEvent.get(registration.eventId);
    if (
      !current ||
      (registration.registrationDate || 0) > (current.registrationDate || 0)
    ) {
      registrationsByEvent.set(registration.eventId, registration);
    }
  });

  return registrationsByEvent;
};

const inferInvoiceTypeFromFilename = (filename?: string): "Paid" | "Due" => {
  const normalized = (filename || "").toLowerCase();
  return normalized.startsWith("paid_invoice") ? "Paid" : "Due";
};

const mergeInvoiceEntries = (
  history: IInvoiceHistory[],
  blobFiles: BlobInvoiceFile[],
): InvoiceCandidate[] => {
  const combined: InvoiceCandidate[] = history.map((entry) => ({
    ...entry,
    invoice: entry.invoice,
    invoiceUrl: entry.invoiceUrl,
  }));

  blobFiles.forEach((file) => {
    const existingEntry = combined.find(
      (entry) => entry.invoice === file.name || entry.invoiceUrl === file.url,
    );

    if (existingEntry) {
      if (!existingEntry.invoiceUrl) {
        existingEntry.invoiceUrl = file.url;
      }
      return;
    }

    combined.push({
      meetingId: "",
      contactId: "",
      type: inferInvoiceTypeFromFilename(file.name),
      amount: 0,
      currency: "USD",
      invoice: file.name,
      invoiceUrl: file.url,
      dateSent: file.dateCreated,
    });
  });

  return combined.sort((left, right) => {
    const leftDate = left.dateSent ? new Date(left.dateSent).getTime() : 0;
    const rightDate = right.dateSent ? new Date(right.dateSent).getTime() : 0;
    return rightDate - leftDate;
  });
};

const EventListSection = ({
  title,
  events,
  onRegister,
  loading,
  emptyMessage,
}: EventListSectionProps) => {
  return (
    <div className="flex min-w-0 flex-col gap-2 min-[1300px]:min-h-[9rem] min-[1300px]:gap-3">
      <div className="flex items-center gap-2">
        <span
          className="text-[14px] font-semibold leading-5 min-[1300px]:whitespace-nowrap min-[1300px]:text-[15px]"
          style={{ color: "var(--Gray-Font, #474F56)" }}
        >
          {title}
        </span>
        <div className="h-px flex-1 bg-[#e6eaee]" />
      </div>

      {loading ? (
        <div className="flex flex-col gap-2 min-[1300px]:gap-3">
          {[0, 1].map((item) => (
            <div
              key={item}
              className="animate-pulse rounded-lg border border-[#eef1f4] p-2.5 min-[1300px]:p-3"
            >
              <div className="mb-2 h-3 w-24 rounded bg-[#eef1f4]" />
              <div className="h-4 w-full rounded bg-[#eef1f4]" />
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#d6dde5] bg-[#fafbfc] px-3 py-3 text-[13px] text-[#69717a] min-[1300px]:px-3.5 min-[1300px]:py-3.5 min-[1300px]:text-sm">
          {emptyMessage}
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-2 min-[1300px]:gap-3">
          {events.map((event, index) => (
            <div
              key={event.id}
              className={`min-w-0 flex flex-col gap-1.5 ${index < events.length - 1
                ? "border-b border-[#eef1f4] pb-2 min-[1300px]:pb-3"
                : ""
                }`}
            >
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[11px] font-normal leading-normal min-[1300px]:text-[12px]"
                    style={{ color: "var(--Gray-Font, #474F56)" }}
                  >
                    {formatMyWsgEventDate(event, formatSectionDate)}
                  </div>
                  <div
                    className="line-clamp-1 text-[13px] font-semibold leading-snug min-[1300px]:text-[14px]"
                    style={{ color: "var(--Gray-Font, #474F56)" }}
                    title={event.title}
                  >
                    {event.title}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => event.id && onRegister(event.id)}
                  className="w-fit shrink-0 self-start rounded-[4px] bg-[#e8f1ff] px-1.5 py-0.5 text-[12px] font-normal leading-normal underline transition-colors hover:bg-[#dceaff] min-[1300px]:text-[13px]"
                  style={{
                    color: "var(--accent-DEFAULT, #0070CD)",
                    textDecorationSkipInk: "none",
                    textUnderlinePosition: "from-font",
                  }}
                >
                  Register here
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EventCard = ({
  data,
  groupData,
  isSingleLayout,
  isDownloading,
  onDownloadInvoice,
  onRegister,
  onModifyRegistration,
  onCompletePayment,
}: EventCardProps) => {
  const groupFallback = getGroupFallbackImage(data.event, groupData);
  const groupPhotoFallback = (data.event as any).groupPhotoFallback;
  const defaultImage = getDefaultEventImage(data.event);
  const countdownBadge = getEventCountdownBadge(data.event);
  const primaryImage = getRegisteredEventImage(
    data.event,
    groupPhotoFallback,
    groupFallback,
  );
  const locationText = formatEventLocation(data.event);
  const hasRegistration = Boolean(data.registration);
  const paymentPending =
    hasRegistration &&
    shouldShowPaymentPending(data.registration?.paymentStatus);
  const paymentPaid = hasRegistration && !paymentPending;

  return (
    <div
      className={`flex h-full flex-col overflow-hidden rounded-[14px] border border-[#d7d7d7] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.08)]`}
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <div
        className="relative z-10 overflow-visible bg-white px-3 py-2.5 text-[14px] font-semibold text-[#474F56]"
        style={{ backgroundColor: "#FFFFFF", color: "#474F56" }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex min-w-0 items-center gap-1.5 font-semibold">
            <IconCalendar className="h-4 w-4 shrink-0 text-[#474F56]" />
            {formatMyWsgEventDate(data.event, formatEventDateInTimeZone)}
          </span>
          <span
            className="ml-auto shrink-0 rounded px-2 py-1 text-xs font-medium"
            style={
              countdownBadge.tone === "live"
                ? { backgroundColor: "#FDF0EF", color: "#C83C27" }
                : { backgroundColor: "#F1F1F1", color: "#474F56" }
            }
          >
            {countdownBadge.label}
          </span>
        </div>
      </div>

      <div
        className="flex flex-1 flex-col bg-white pb-3 pt-1"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="relative overflow-hidden">
          <img
            src={primaryImage}
            alt={data.event.title || "Event"}
            className="block w-full object-contain"
            style={{ maxHeight: "161px", height: "auto" }}
            onError={(event) => {
              const target = event.currentTarget;
              if (
                target.dataset.fallbackApplied !== "true" &&
                (groupPhotoFallback || groupFallback)
              ) {
                target.dataset.fallbackApplied = "true";
                target.src = groupPhotoFallback || groupFallback || defaultImage;
                return;
              }
              target.src = defaultImage;
            }}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col items-center gap-1 px-5 pb-1 pt-3 text-center">
          <div
            className={`font-semibold leading-tight text-[#474F56] ${isSingleLayout
              ? "line-clamp-2 text-[1.15rem]"
              : "line-clamp-2 text-[1rem]"
              }`}
            style={{ color: "#474F56" }}
          >
            {data.event.title}
          </div>

          <div
            className="flex min-w-0 items-center justify-center gap-1.5 text-[0.9rem] leading-tight text-[#474F56]"
            style={{ color: "#474F56" }}
          >
            <IconLocation className="h-4 w-4 shrink-0 text-[#474F56]" />
            <span className="line-clamp-1">{locationText}</span>
          </div>

          {hasRegistration && (
            <div>
              <button
                type="button"
                onClick={() => onDownloadInvoice(data)}
                disabled={isDownloading}
                className="inline-flex min-h-[24px] items-center justify-center gap-1.5 text-center text-[14px] font-normal leading-[18px] text-[#0070CD] underline transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
              >
                <IconDownload className="h-4 w-4 shrink-0" />
                <span>
                  {isDownloading
                    ? "Downloading..."
                    : getInvoiceLabel(data.registration?.paymentStatus)}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-[#eef1f4] bg-white px-3 pb-3 pt-3">
        <div className="grid grid-cols-1 gap-2">
          {!hasRegistration ? (
            <button
              type="button"
              onClick={() => data.event.id && onRegister(data.event.id)}
              className="min-h-[56px] rounded-md bg-[#0d67c4] p-2 text-center text-[14px] font-medium leading-[18px] text-white transition-colors hover:bg-[#0957a7]"
            >
              Register Now
            </button>
          ) :
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => data.event.id && onModifyRegistration(data.event.id)}
                className="min-h-[56px] rounded-md bg-[#0d67c4] p-2 text-center text-[14px] font-medium leading-[18px] text-white transition-colors hover:bg-[#0957a7]"
              >
                Modify Registration
              </button>
              {paymentPending ? (
                <button
                  type="button"
                  onClick={() => data.event.id && onCompletePayment(data.event.id)}
                  className="min-h-[56px] rounded-md bg-[#c83c27] p-2 text-center text-[14px] font-medium leading-[18px] text-white transition-colors hover:bg-[#ac301d]"
                >
                  Payment Pending
                </button>
              ) : (
                paymentPaid && (
                  <button
                    type="button"
                    disabled
                    className="min-h-[56px] cursor-default rounded-md bg-[#2f7d32] p-2 text-center text-[14px] font-medium leading-[18px] text-white"
                  >
                    Payment Paid
                  </button>
                )
              )}
            </div>
          }
        </div>
      </div>
    </div>
  );
};

const MyEvents = () => {
  const navigate = useNavigate();
  const { loggedPerson } = useContext(AuthContext);
  const { groupData } = UseGroupList();
  const registeredEventsCarouselRef = useRef<Carousel | null>(null);
  const [loading, setLoading] = useState(true);
  const [carouselEvents, setCarouselEvents] = useState<EventCardData[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<IEvent[]>([]);
  const [preferenceEvents, setPreferenceEvents] = useState<IEvent[]>([]);
  const [downloadingRegistrationId, setDownloadingRegistrationId] = useState<
    string | null
  >(null);

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      if (!loggedPerson?.id) {
        if (isMounted) {
          setLoading(false);
          setCarouselEvents([]);
          setUpcomingEvents([]);
          setPreferenceEvents([]);
        }
        return;
      }

      setLoading(true);

      try {
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const queryStartTimestamp = currentTimestamp - ONE_DAY_IN_SECONDS;
        const [
          registrations,
          upcomingEventResponse,
          liveEventResponse,
          groupMemberships,
          groups,
          firm,
        ] =
          await Promise.all([
            getRegistrationsByPerson(loggedPerson.id),
            getEventsByFilter(
              {
                ...WSG_UPCOMING_VISIBILITY_FILTER,
                startDate: {
                  value: `${queryStartTimestamp} - ${TEN_YEARS_IN_SECONDS}`,
                  comparisonType: "BETWEEN_TS",
                },
              },
              { field: "startDate", order: "asc" },
            ),
            getEventsByFilter(
              {
                ...WSG_UPCOMING_VISIBILITY_FILTER,
                endDate: {
                  value: currentTimestamp,
                  comparisonType: "GREATER_THAN",
                },
              },
              { field: "startDate", order: "asc" },
            ),
            getGroupMembershipsByPersonId(loggedPerson.id).catch(() => []),
            getAllGroups().catch(() => []),
            loggedPerson.firm
              ? getFirmById(loggedPerson.firm).catch(() => null)
              : Promise.resolve(null),
          ]);
        if (!isMounted) {
          return;
        }

        const registrationsByEvent = mergeRegistrationsByEvent(
          registrations || [],
        );
        const registeredEventIds = new Set(registrationsByEvent.keys());

        const officialEventsById = new Map<string, IEvent>();
        [...(upcomingEventResponse.data || []), ...(liveEventResponse.data || [])]
          .forEach((event) => {
            if (event.id) {
              officialEventsById.set(event.id, event);
            }
          });

        const missingRegisteredEventIds = Array.from(registeredEventIds).filter(
          (eventId) => !officialEventsById.has(eventId),
        );
        const registeredOnlyEvents = (
          await Promise.all(
            missingRegisteredEventIds.map((eventId) =>
              getEventById(eventId, false).catch(() => null),
            ),
          )
        ).filter((event): event is IEvent => Boolean(event?.id));

        if (!isMounted) {
          return;
        }

        const isActiveCurrentEvent = (event: IEvent): boolean =>
          Boolean(event.id) &&
          event.isDeleted !== true &&
          isCurrentEvent(event);

        const sortByStartDate = (left: IEvent, right: IEvent): number => {
          const leftStartDate = left.startDate ?? Number.MAX_SAFE_INTEGER;
          const rightStartDate = right.startDate ?? Number.MAX_SAFE_INTEGER;
          return leftStartDate - rightStartDate;
        };

        const visibleUpcomingEvents = Array.from(officialEventsById.values())
          .filter(
            (event) => isActiveCurrentEvent(event) && shouldShowMyWsgEvent(event),
          )
          .sort(sortByStartDate);

        const carouselEventsById = new Map<string, IEvent>();
        visibleUpcomingEvents.forEach((event) => {
          carouselEventsById.set(event.id as string, event);
        });
        registeredOnlyEvents
          .filter(isActiveCurrentEvent)
          .forEach((event) => {
            carouselEventsById.set(event.id as string, event);
          });

        const nextCarouselEvents = Array.from(carouselEventsById.values())
          .sort(sortByStartDate)
          .map((event) => ({
            event,
            registration: registrationsByEvent.get(event.id as string),
          }));

        const unregisteredUpcomingEvents = visibleUpcomingEvents.filter(
          (event) => event.id && !registeredEventIds.has(event.id),
        );

        setCarouselEvents(nextCarouselEvents);
        setUpcomingEvents(
          unregisteredUpcomingEvents.slice(0, MAX_UPCOMING_EVENTS),
        );
        setPreferenceEvents(
          getPreferenceEvents({
            events: unregisteredUpcomingEvents,
            groups,
            memberships: groupMemberships,
            personFirmId: loggedPerson.firm,
            personFirm: firm,
            maxEvents: MAX_PREFERENCE_EVENTS,
          }),
        );
      } catch (error) {
        console.error("Error loading MyWSG events:", error);

        if (isMounted) {
          setCarouselEvents([]);
          setUpcomingEvents([]);
          setPreferenceEvents([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, [loggedPerson?.id, loggedPerson?.firm]);

  const handleDownloadInvoice = async ({
    event,
    registration,
  }: EventCardData) => {
    if (!event.id || !registration?.id || !loggedPerson?.id) {
      toastWarn("Invoice file is not available for this registration.");
      return;
    }

    try {
      setDownloadingRegistrationId(registration.id);

      const [history, blobFiles] = await Promise.all([
        getInvoiceHistory(event.id, registration.id),
        listInvoiceFiles(loggedPerson.id, event.id),
      ]);

      const mergedEntries = mergeInvoiceEntries(
        history,
        blobFiles.map((file) => ({
          name: file.name,
          url: file.url,
          dateCreated: file.dateCreated,
        })),
      );

      const desiredType = getInvoiceTypeForStatus(registration.paymentStatus);
      const latestInvoice = mergedEntries.find(
        (entry) =>
          entry.type === desiredType &&
          entry.invoiceUrl &&
          entry.invoice &&
          entry.invoice.trim().length > 0,
      );

      if (!latestInvoice?.invoiceUrl || !latestInvoice.invoice) {
        toastWarn(`No ${desiredType.toLowerCase()} invoice is available yet.`);
        return;
      }

      await downloadInvoicePDF(latestInvoice.invoiceUrl, latestInvoice.invoice);
    } catch (error: any) {
      console.error("Error downloading invoice:", error);
      toastError(
        error?.message || "The invoice could not be downloaded right now.",
      );
    } finally {
      setDownloadingRegistrationId(null);
    }
  };

  const renderTopSection = () => {
    const hasMultipleCarouselEvents = carouselEvents.length > 1;
    const isSingleCarouselEvent = carouselEvents.length === 1;

    if (loading) {
      return (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {[0, 1].map((item) => (
            <div
              key={item}
              className="animate-pulse rounded-xl border border-[#d6dde5] p-4"
            >
              <div className="mb-4 h-4 w-40 rounded bg-[#eef1f4]" />
              <div className="mb-4 h-48 rounded-lg bg-[#eef1f4]" />
              <div className="mb-3 h-6 w-4/5 rounded bg-[#eef1f4]" />
              <div className="h-10 w-40 rounded bg-[#eef1f4]" />
            </div>
          ))}
        </div>
      );
    }

    if (carouselEvents.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-[#d6dde5] bg-[#fafbfc] px-5 py-6">
          <div className="mb-4 text-base leading-relaxed text-[#5d6670]">
            No upcoming events are available right now. Click here to discover
            more:
          </div>
          <button
            type="button"
            onClick={() => navigate("/events")}
            className="rounded-md bg-[#0d67c4] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0957a7]"
          >
            All Events
          </button>
        </div>
      );
    }

    return (
      <div className="relative">
        {hasMultipleCarouselEvents && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center">
              <div className="pointer-events-auto pl-1">
                <CarouselNavButton
                  direction="left"
                  onClick={() => registeredEventsCarouselRef.current?.previous(1)}
                />
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center">
              <div className="pointer-events-auto pr-1">
                <CarouselNavButton
                  direction="right"
                  onClick={() => registeredEventsCarouselRef.current?.next(1)}
                />
              </div>
            </div>
          </>
        )}

        <div>
          <div className="-mx-2">
            <Carousel
              ref={registeredEventsCarouselRef}
              responsive={REGISTERED_EVENTS_CAROUSEL_RESPONSIVE}
              infinite={hasMultipleCarouselEvents}
              arrows={false}
              swipeable={false}
              draggable={false}
              showDots={false}
              ssr={false}
              autoPlay={hasMultipleCarouselEvents}
              autoPlaySpeed={REGISTERED_EVENTS_AUTOPLAY_SPEED}
              pauseOnHover={true}
              shouldResetAutoplay={false}
              keyBoardControl={hasMultipleCarouselEvents}
              customTransition="transform 500ms ease"
              transitionDuration={500}
              sliderClass="items-stretch"
              itemClass="px-2"
              containerClass="pb-1"
            >
              {carouselEvents.map((item) => (
                <div
                  key={
                    item.registration?.id ||
                    item.event.id ||
                    `${item.event.title}-${item.event.startDate}`
                  }
                  className="h-full"
                >
                  <EventCard
                    data={item}
                    groupData={groupData}
                    isSingleLayout={isSingleCarouselEvent}
                    isDownloading={
                      downloadingRegistrationId === item.registration?.id
                    }
                    onDownloadInvoice={handleDownloadInvoice}
                    onRegister={(eventId) =>
                      navigate(`/events/${eventId}/registration`)
                    }
                    onModifyRegistration={(eventId) =>
                      navigate(`/events/${eventId}/registration/form`)
                    }
                    onCompletePayment={(eventId) =>
                      navigate(`/events/${eventId}/registration/payment`)
                    }
                  />
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col gap-4 text-gray-font">
      {renderTopSection()}

      <div className="grid grid-cols-1 gap-3 min-[1300px]:grid-cols-2 min-[1300px]:gap-4">
        <EventListSection
          title="Upcoming Events"
          events={upcomingEvents}
          onRegister={(eventId) => navigate(`/events/${eventId}/registration`)}
          loading={loading}
          emptyMessage="No upcoming events are available right now."
        />
        <EventListSection
          title="Events Based On Your Preferences"
          events={preferenceEvents}
          onRegister={(eventId) => navigate(`/events/${eventId}/registration`)}
          loading={loading}
          emptyMessage="No events match your preferences right now."
        />
      </div>
    </div>
  );
};

export default MyEvents;
